import {
  type ReactNode,
  Fragment,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ExternalLink, Lock } from "lucide-react";
import { Link } from "react-router-dom";

import { PurchaseOrderHubLinkWithMegaventory } from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { HoverHintContent } from "@/components/ui/title-help";
import { megaventoryUrlFor, workOrderHubHref } from "@/pages/manufacturing/workOrdersPageModel";
import { stripAltTlaSkuPrefixForDisplay } from "@/lib/mrp";
import { cn } from "@/lib/utils";
import {
  computePointAnchoredContextMenu,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";

import { formatTlaPlanningDate, TLA_PLANNING_NUM_CELL_CLASS } from "./tlaPlanningShared";
import {
  KodiakAssignedPrefixChips,
  KodiakFamilyChip,
  KodiakPcbaPrefixChipZone,
  kodiakPcbaColumnPrefixAssignments,
  kodiakPcbaSkuCoreFeature,
  kodiakPrefixAssignmentHoverKey,
  kodiakPrefixHoverHasDisplayableRows,
  kodiakTlaRowPrefixAssignments,
  kodiakTlaSkuShortLabel,
  resolveKodiakTlaSkuChipAppearance,
  type KodiakPcbaPrefixAssignment,
  type KodiakPrefixAssignmentBoard,
  type KodiakPrefixAssignmentHoverDetail,
  type KodiakPrefixAssignmentHoverRow,
  type KodiakPrefixAssignmentViewModel,
  type KodiakPrefixDragPayload,
  type KodiakTlaPrefixDropTarget,
  useKodiakTlaPrefixDropRow,
} from "./kodiakPrefixAssignmentUi";

export interface KodiakPcbaPrefixBreakdownEntry {
  prefix: string;
  serial_fci_count: number;
  qc_incident_count: number;
}

export type KodiakTlaPcbaMatchRowKind = "standard" | "unallocated_qc";

export interface KodiakTlaPcbaMatchRow {
  row_kind: KodiakTlaPcbaMatchRowKind;
  po_number: string;
  po_date: string | null;
  po_quantity: number;
  purchase_order_id: number | null;
  purchase_order_mv_id: number | null;
  pcba_work_order_number: string;
  pcba_build_order_id: number | null;
  pcba_build_order_mv_id: number | null;
  pcba_sku: string;
  pcba_work_order_qty: number;
  pcba_prefix_estimate: string | null;
  tla_work_order_number: string | null;
  tla_build_order_id: number | null;
  tla_build_order_mv_id: number | null;
  tla_sku: string | null;
  tla_work_order_qty: number | null;
  tla_po_number?: string | null;
  tla_purchase_order_id?: number | null;
  tla_purchase_order_mv_id?: number | null;
  unique_serial_count: number;
  serial_begin_date: string | null;
  serial_end_date: string | null;
  pcba_prefix_breakdown: KodiakPcbaPrefixBreakdownEntry[];
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

const KODIAK_LINE_COMPLETE_LOCK_MENU_WIDTH_PX = 14 * 16;
/** Single menu row: py-2 button (~36px) + py-1 chrome + border. Used for placement only. */
const KODIAK_LINE_COMPLETE_LOCK_MENU_HEIGHT_PX = 48;

const EMPTY_KODIAK_LINE_COMPLETE_LOCK_IDS = new Set<number>();

export type KodiakLineCompleteLockContextMenuState = {
  clientX: number;
  clientY: number;
  tlaBuildOrderId: number;
  locked: boolean;
} | null;

type KodiakLineCompleteLockContextMenu = KodiakLineCompleteLockContextMenuState;

function KodiakTlaSerialCountCell({
  row,
  lineCompleteLockIds,
  lineCompleteLockAdminAllowed,
  lineCompleteLockPendingBuildOrderId,
  onRequestContextMenu,
}: {
  row: KodiakTlaPcbaMatchRow;
  lineCompleteLockIds: ReadonlySet<number>;
  lineCompleteLockAdminAllowed: boolean;
  lineCompleteLockPendingBuildOrderId: number | null;
  onRequestContextMenu: (
    event: React.MouseEvent<HTMLTableCellElement>,
    target: { tlaBuildOrderId: number; locked: boolean },
  ) => void;
}): JSX.Element {
  if (row.row_kind === "unallocated_qc") {
    return (
      <td className={TLA_PLANNING_NUM_CELL_CLASS}>{formatNumber(row.unique_serial_count)}</td>
    );
  }

  const tlaBuildOrderId = row.tla_build_order_id == null ? null : Number(row.tla_build_order_id);
  const locked =
    tlaBuildOrderId != null &&
    Number.isFinite(tlaBuildOrderId) &&
    lineCompleteLockIds.has(tlaBuildOrderId);
  const orderedQty = row.tla_work_order_qty;
  const rowLockPending =
    lineCompleteLockPendingBuildOrderId != null &&
    tlaBuildOrderId != null &&
    lineCompleteLockPendingBuildOrderId === tlaBuildOrderId;
  const canManageLock =
    lineCompleteLockAdminAllowed &&
    !rowLockPending &&
    tlaBuildOrderId != null &&
    Number.isFinite(tlaBuildOrderId) &&
    orderedQty != null &&
    orderedQty > 0;

  const display = locked && orderedQty != null ? (
    <span className="inline-flex items-center justify-end gap-1">
      <span>
        {formatNumber(row.unique_serial_count)} / {formatNumber(orderedQty)}
      </span>
      <Lock className="h-3.5 w-3.5 shrink-0 text-red-600" aria-label="Complete lock" />
    </span>
  ) : (
    formatNumber(row.unique_serial_count)
  );

  return (
    <td
      className={cn(
        TLA_PLANNING_NUM_CELL_CLASS,
        canManageLock ? "cursor-context-menu" : undefined,
      )}
      onContextMenu={(event) => {
        if (!canManageLock || tlaBuildOrderId == null) return;
        event.preventDefault();
        event.stopPropagation();
        onRequestContextMenu(event, { tlaBuildOrderId, locked });
      }}
    >
      {display}
    </td>
  );
}

export function KodiakLineCompleteLockContextMenuPanel({
  menu,
  pendingBuildOrderId,
  onClose,
  onToggleLock,
}: {
  menu: KodiakLineCompleteLockContextMenu;
  pendingBuildOrderId: number | null;
  onClose: () => void;
  onToggleLock: (tlaBuildOrderId: number, lock: boolean) => void;
}): JSX.Element | null {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuBox, setMenuBox] = useState<AnchoredMenuPanelBox | null>(null);

  useLayoutEffect(() => {
    if (menu == null) {
      setMenuBox(null);
      return;
    }
    setMenuBox(
      computePointAnchoredContextMenu({
        clientX: menu.clientX,
        clientY: menu.clientY,
        menuWidth: KODIAK_LINE_COMPLETE_LOCK_MENU_WIDTH_PX,
        estimatedMenuHeight: KODIAK_LINE_COMPLETE_LOCK_MENU_HEIGHT_PX,
      }),
    );
    const dismissOnLayoutShift = (): void => {
      onClose();
    };
    window.addEventListener("resize", dismissOnLayoutShift);
    window.addEventListener("scroll", dismissOnLayoutShift, true);
    window.visualViewport?.addEventListener("resize", dismissOnLayoutShift);
    window.visualViewport?.addEventListener("scroll", dismissOnLayoutShift);
    return () => {
      window.removeEventListener("resize", dismissOnLayoutShift);
      window.removeEventListener("scroll", dismissOnLayoutShift, true);
      window.visualViewport?.removeEventListener("resize", dismissOnLayoutShift);
      window.visualViewport?.removeEventListener("scroll", dismissOnLayoutShift);
    };
  }, [menu, onClose]);

  useEffect(() => {
    if (menu == null) return;
    const onMouseDown = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (menuRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menu, onClose]);

  if (menu == null || menuBox == null) {
    return null;
  }

  const rowPending = pendingBuildOrderId === menu.tlaBuildOrderId;

  return createPortal(
    <div
      ref={menuRef}
      className="fixed rounded-md border border-slate-200 bg-white py-1 shadow-lg"
      style={{
        left: menuBox.left,
        top: menuBox.top,
        width: menuBox.width,
        zIndex: FLOATING_POPOVER_Z_INDEX,
      }}
      role="menu"
    >
      <button
        type="button"
        className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-50"
        disabled={rowPending}
        role="menuitem"
        onClick={() => {
          onToggleLock(menu.tlaBuildOrderId, !menu.locked);
          onClose();
        }}
      >
        {menu.locked ? "Clear fully assigned" : "Count as fully assigned"}
      </button>
    </div>,
    document.body,
  );
}

export interface KodiakTlaWorkOrderOption {
  tla_build_order_id: number;
  tla_work_order_number: string;
  tla_sku: string;
  tla_work_order_qty: number | null;
  purchase_order_id: number | null;
  po_number: string;
  pcba_build_order_id: number | null;
  pcba_work_order_number: string | null;
  core_feature: string | null;
}

export function kodiakTlaCoreFeatureFromSku(tlaSku: string): string | null {
  const normalized = tlaSku.trim().replace(/^ALT-/i, "");
  const match = normalized.match(/-([NBQ])-TLA-/);
  return match?.[1] ?? null;
}

export function buildKodiakTlaWorkOrderOptions(rows: KodiakTlaPcbaMatchRow[]): KodiakTlaWorkOrderOption[] {
  const byBuildOrderId = new Map<number, KodiakTlaWorkOrderOption>();
  for (const row of rows) {
    if (row.row_kind === "unallocated_qc") continue;
    if (row.tla_build_order_id == null || row.tla_sku == null || row.tla_work_order_number == null) {
      continue;
    }
    const existing = byBuildOrderId.get(row.tla_build_order_id);
    if (existing != null) continue;
    byBuildOrderId.set(row.tla_build_order_id, {
      tla_build_order_id: row.tla_build_order_id,
      tla_work_order_number: row.tla_work_order_number,
      tla_sku: row.tla_sku,
      tla_work_order_qty: row.tla_work_order_qty,
      purchase_order_id: row.purchase_order_id,
      po_number: row.po_number,
      pcba_build_order_id: row.pcba_build_order_id,
      pcba_work_order_number: row.pcba_work_order_number,
      core_feature: kodiakTlaCoreFeatureFromSku(row.tla_sku),
    });
  }
  return [...byBuildOrderId.values()].sort((left, right) => {
    const poCompare = left.po_number.localeCompare(right.po_number);
    if (poCompare !== 0) return poCompare;
    return left.tla_work_order_number.localeCompare(right.tla_work_order_number);
  });
}

export function filterKodiakTlaWorkOrderOptionsForCore(
  options: KodiakTlaWorkOrderOption[],
  coreFeature: string,
): KodiakTlaWorkOrderOption[] {
  if (coreFeature === "?") return options;
  return options.filter((option) => option.core_feature === coreFeature);
}

export function formatKodiakTlaWorkOrderOptionLabel(
  option: KodiakTlaWorkOrderOption,
  formatTlaSku: (sku: string) => string,
): string {
  const qty =
    option.tla_work_order_qty == null ? "" : ` · qty ${option.tla_work_order_qty.toLocaleString("en-US")}`;
  return `${option.po_number} · ${option.tla_work_order_number} · ${formatTlaSku(option.tla_sku)}${qty}`;
}

function kodiakPcbaGroupKey(row: KodiakTlaPcbaMatchRow): string {
  return `${row.po_number}|${row.pcba_work_order_number}`;
}

export function kodiakTlaShortLabelsForGroupRows(groupRows: KodiakTlaPcbaMatchRow[]): string[] {
  const labels = new Set<string>();
  for (const row of groupRows) {
    if (row.row_kind === "unallocated_qc") {
      const coreFeature = kodiakPcbaCoreFeature(row.pcba_sku);
      if (coreFeature != null) labels.add(coreFeature === "W" ? "W-TLA" : `${coreFeature}-TLA-?`);
      continue;
    }
    if (row.tla_sku == null) continue;
    labels.add(kodiakTlaSkuShortLabel(row.tla_sku));
  }
  return [...labels];
}

function kodiakPcbaCoreFeature(pcbaSku: string): string | null {
  const fi3cMatch = pcbaSku.match(/^15-1607-([NBQ])-/i);
  if (fi3cMatch?.[1] != null) {
    return fi3cMatch[1].toUpperCase();
  }
  if (/^15-1485-W-/i.test(pcbaSku.trim())) {
    return "W";
  }
  return null;
}

export function buildKodiakPrefixHoverFromMatchGroup(
  assignment: KodiakPcbaPrefixAssignment,
  groupRows: KodiakTlaPcbaMatchRow[],
): KodiakPrefixAssignmentHoverDetail | undefined {
  const anchorRow = groupRows[0];
  if (anchorRow == null) return undefined;
  const pcbaSku = anchorRow.pcba_sku;
  const prefixBreakdown = anchorRow.pcba_prefix_breakdown.find(
    (entry) => entry.prefix === assignment.pcba_prefix,
  );
  const tlaRowsWithSerials = groupRows.filter(
    (matchRow) =>
      matchRow.row_kind !== "unallocated_qc" &&
      matchRow.tla_sku != null &&
      matchRow.unique_serial_count > 0,
  );
  const usePerLineSerialCounts = tlaRowsWithSerials.length > 1;
  const rows: KodiakPrefixAssignmentHoverRow[] = [];

  for (const matchRow of groupRows) {
    if (matchRow.row_kind === "unallocated_qc") {
      if (matchRow.unique_serial_count <= 0) continue;
      const coreFeature = kodiakPcbaCoreFeature(pcbaSku);
      rows.push({
        pcba_sku: pcbaSku,
        tla_sku: coreFeature == null ? "—" : coreFeature === "W" ? "W-TLA" : `${coreFeature}-TLA-?`,
        serial_fci_count: 0,
        qc_incident_count: matchRow.unique_serial_count,
        serial_begin_date: matchRow.serial_begin_date,
        serial_end_date: matchRow.serial_end_date,
      });
      continue;
    }
    if (matchRow.tla_sku == null || matchRow.unique_serial_count <= 0) continue;
    rows.push({
      pcba_sku: pcbaSku,
      tla_sku: kodiakTlaSkuShortLabel(matchRow.tla_sku),
      serial_fci_count: usePerLineSerialCounts
        ? matchRow.unique_serial_count
        : (prefixBreakdown?.serial_fci_count ?? matchRow.unique_serial_count),
      qc_incident_count: usePerLineSerialCounts ? 0 : (prefixBreakdown?.qc_incident_count ?? 0),
      serial_begin_date: matchRow.serial_begin_date,
      serial_end_date: matchRow.serial_end_date,
    });
  }

  if (rows.length === 0) return undefined;
  return {
    purchase_order_id: assignment.purchase_order_id,
    pcba_build_order_id: assignment.pcba_build_order_id,
    pcba_prefix: assignment.pcba_prefix,
    rows,
  };
}

export function buildKodiakPrefixHoverFromPrefixProfile(
  assignment: KodiakPcbaPrefixAssignment,
  pcbaSku: string,
  board: KodiakPrefixAssignmentBoard | undefined,
): KodiakPrefixAssignmentHoverDetail | undefined {
  const discovered = board?.discovered_prefixes.find(
    (entry) =>
      entry.prefix === assignment.pcba_prefix && entry.core_feature === assignment.core_feature,
  );
  if (discovered == null) return undefined;

  const discoveredHover = board?.discovered_prefix_hover.find(
    (entry) =>
      entry.pcba_prefix === assignment.pcba_prefix && entry.core_feature === assignment.core_feature,
  );
  const fallbackTlaSku =
    discoveredHover?.rows.find((row) => row.tla_sku.trim() !== "" && row.tla_sku !== "—")?.tla_sku ??
    (discovered.core_feature == null
      ? "—"
      : discovered.core_feature === "W"
        ? "W-TLA"
        : `${discovered.core_feature}-TLA-?`);

  const peerHover = board?.assignment_hover.find(
    (entry) =>
      entry.pcba_prefix === assignment.pcba_prefix &&
      entry.purchase_order_id !== assignment.purchase_order_id,
  );
  const rows: KodiakPrefixAssignmentHoverRow[] = [];

  for (const peerRow of peerHover?.rows ?? []) {
    if (peerRow.serial_fci_count <= 0 && peerRow.qc_incident_count <= 0) continue;
    rows.push({
      pcba_sku: pcbaSku,
      tla_sku: peerRow.tla_sku,
      serial_fci_count: peerRow.serial_fci_count,
      qc_incident_count: peerRow.qc_incident_count,
      serial_begin_date: peerRow.serial_begin_date,
      serial_end_date: peerRow.serial_end_date,
    });
  }

  const qcInRows = rows.reduce((sum, row) => sum + row.qc_incident_count, 0);
  if (discovered.qc_incident_count > qcInRows) {
    rows.push({
      pcba_sku: pcbaSku,
      tla_sku: fallbackTlaSku,
      serial_fci_count: 0,
      qc_incident_count: discovered.qc_incident_count - qcInRows,
      serial_begin_date: discovered.serial_begin_date,
      serial_end_date: discovered.serial_end_date,
    });
  }

  if (rows.length === 0 && discovered.serial_fci_count > 0) {
    const coreFeature = kodiakPcbaCoreFeature(pcbaSku);
    for (const hoverRow of discoveredHover?.rows ?? []) {
      if (hoverRow.serial_fci_count <= 0) continue;
      if (coreFeature != null && !hoverRow.tla_sku.startsWith(`${coreFeature}-TLA`)) continue;
      rows.push({
        pcba_sku: pcbaSku,
        tla_sku: hoverRow.tla_sku,
        serial_fci_count: hoverRow.serial_fci_count,
        qc_incident_count: 0,
        serial_begin_date: hoverRow.serial_begin_date,
        serial_end_date: hoverRow.serial_end_date,
      });
    }
  }

  if (rows.length === 0 && discovered.qc_incident_count > 0) {
    rows.push({
      pcba_sku: pcbaSku,
      tla_sku: fallbackTlaSku,
      serial_fci_count: 0,
      qc_incident_count: discovered.qc_incident_count,
      serial_begin_date: discovered.serial_begin_date,
      serial_end_date: discovered.serial_end_date,
    });
  }

  if (rows.length === 0) return undefined;
  return {
    purchase_order_id: assignment.purchase_order_id,
    pcba_build_order_id: assignment.pcba_build_order_id,
    pcba_prefix: assignment.pcba_prefix,
    rows,
  };
}

export function enrichKodiakPrefixViewModelFromMatchGroups(
  viewModel: KodiakPrefixAssignmentViewModel,
  assignments: KodiakPcbaPrefixAssignment[],
  groups: KodiakPcbaRowGroup[],
  board?: KodiakPrefixAssignmentBoard,
): KodiakPrefixAssignmentViewModel {
  const hover_by_assignment_key = new Map(viewModel.hover_by_assignment_key);
  const assignmentCountByPcbaBuildOrderId = new Map<number, number>();
  for (const assignment of assignments) {
    const pcbaBuildOrderId = Number(assignment.pcba_build_order_id);
    assignmentCountByPcbaBuildOrderId.set(
      pcbaBuildOrderId,
      (assignmentCountByPcbaBuildOrderId.get(pcbaBuildOrderId) ?? 0) + 1,
    );
  }
  for (const assignment of assignments) {
    const key = kodiakPrefixAssignmentHoverKey(
      assignment.purchase_order_id,
      assignment.pcba_build_order_id,
      assignment.pcba_prefix,
      assignment.core_feature,
    );
    const existing = hover_by_assignment_key.get(key);
    if (kodiakPrefixHoverHasDisplayableRows(existing)) continue;

    const group = groups.find((entry) => {
      const row = entry.rows[0];
      return (
        row != null &&
        Number(row.purchase_order_id) === assignment.purchase_order_id &&
        Number(row.pcba_build_order_id) === assignment.pcba_build_order_id
      );
    });
    const anchorRow = group?.rows[0];
    if (anchorRow == null) continue;

    if (group == null) continue;
    const assignmentCount = assignmentCountByPcbaBuildOrderId.get(Number(assignment.pcba_build_order_id)) ?? 0;
    if (assignmentCount === 1) {
      const matchFallback = buildKodiakPrefixHoverFromMatchGroup(assignment, group.rows);
      if (matchFallback != null) {
        hover_by_assignment_key.set(key, matchFallback);
        continue;
      }
    }

    const profileFallback = buildKodiakPrefixHoverFromPrefixProfile(
      assignment,
      anchorRow.pcba_sku,
      board,
    );
    if (profileFallback != null) {
      hover_by_assignment_key.set(key, profileFallback);
    }
  }
  return { ...viewModel, hover_by_assignment_key };
}

export interface KodiakPcbaRowGroup {
  key: string;
  rows: KodiakTlaPcbaMatchRow[];
  /** Parent row plus TLA child rows when multiple TLAs share one PCBA. */
  layout: "flat" | "grouped";
}

/** Merge sorted flat rows into PO+PCBA groups while preserving list order. */
export function buildKodiakPcbaRowGroups(sortedRows: KodiakTlaPcbaMatchRow[]): KodiakPcbaRowGroup[] {
  const groups: KodiakPcbaRowGroup[] = [];
  for (const row of sortedRows) {
    const key = kodiakPcbaGroupKey(row);
    const previous = groups[groups.length - 1];
    if (previous != null && previous.key === key) {
      previous.rows.push(row);
      continue;
    }
    groups.push({ key, rows: [row], layout: "flat" });
  }
  for (const group of groups) {
    const tlaChildRows = group.rows.filter(
      (entry) => entry.row_kind !== "unallocated_qc" && entry.tla_work_order_number != null,
    );
    const hasUnallocatedQc = group.rows.some((entry) => entry.row_kind === "unallocated_qc");
    group.layout = tlaChildRows.length > 1 || hasUnallocatedQc ? "grouped" : "flat";
  }
  return groups;
}

function KodiakBuildOrderLink({
  buildOrderId,
  buildOrderMvId,
  workOrderNumber,
}: {
  buildOrderId: number | null;
  buildOrderMvId: number | null;
  workOrderNumber: string;
}): JSX.Element {
  const label = workOrderNumber.trim() || "—";
  if (label === "—") return <span>—</span>;
  const megaventoryUrl =
    buildOrderMvId != null ? megaventoryUrlFor({ mv_id: buildOrderMvId, mv_number: workOrderNumber }) : null;
  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <Link
        to={workOrderHubHref({ id: buildOrderId ?? undefined, number: workOrderNumber })}
        className="font-mono text-brand-700 hover:text-brand-900 hover:underline"
      >
        {label}
      </Link>
      {megaventoryUrl != null ? (
        <a
          href={megaventoryUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex shrink-0 text-slate-400 hover:text-brand-600"
          aria-label={`Open build order ${label} in Megaventory`}
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      ) : null}
    </span>
  );
}

function KodiakPurchaseOrderLink({ row }: { row: KodiakTlaPcbaMatchRow }): JSX.Element {
  if (row.purchase_order_id != null) {
    return (
      <PurchaseOrderHubLinkWithMegaventory
        purchaseOrderId={row.purchase_order_id}
        poNumber={row.po_number}
        purchaseOrderMvId={row.purchase_order_mv_id}
      />
    );
  }
  return <span className="font-mono">{row.po_number}</span>;
}

function KodiakPcbaPrefixCell({
  row,
  groupRows,
  prefixViewModel,
  prefixAssignmentDisabled,
  onAssignPrefix,
  onDragStartPrefix,
}: {
  row: KodiakTlaPcbaMatchRow;
  groupRows: KodiakTlaPcbaMatchRow[];
  prefixViewModel: KodiakPrefixAssignmentViewModel;
  prefixAssignmentDisabled: boolean;
  onAssignPrefix: (
    payload: KodiakPrefixDragPayload,
    target: { purchase_order_id: number; pcba_build_order_id: number },
  ) => void;
  onDragStartPrefix: (event: React.DragEvent<HTMLSpanElement>, payload: KodiakPrefixDragPayload) => void;
}): JSX.Element {
  const tlaChildRows = groupRows.filter(
    (entry) => entry.row_kind !== "unallocated_qc" && entry.tla_work_order_number != null,
  );
  const groupIsSplit =
    tlaChildRows.length > 1 || groupRows.some((entry) => entry.row_kind === "unallocated_qc");
  const assignments =
    row.pcba_build_order_id == null
      ? []
      : kodiakPcbaColumnPrefixAssignments(
          row.pcba_build_order_id,
          row,
          groupIsSplit,
          prefixViewModel,
        );

  if (row.row_kind !== "standard" || row.pcba_build_order_id == null) {
    return <span>—</span>;
  }

  return (
    <KodiakPcbaPrefixChipZone
      purchaseOrderId={row.purchase_order_id}
      pcbaBuildOrderId={row.pcba_build_order_id}
      targetCoreFeature={kodiakPcbaSkuCoreFeature(row.pcba_sku)}
      targetTlaShortLabels={kodiakTlaShortLabelsForGroupRows(groupRows)}
      assignments={assignments}
      discovered_by_variant={prefixViewModel.discovered_by_variant}
      discovered_hover_by_variant={prefixViewModel.discovered_hover_by_variant}
      planned_by_variant={prefixViewModel.planned_by_variant}
      hover_by_assignment_key={prefixViewModel.hover_by_assignment_key}
      prefixViewModel={prefixViewModel}
      disabled={prefixAssignmentDisabled}
      onAssignPrefix={onAssignPrefix}
      onDragStartPrefix={onDragStartPrefix}
    />
  );
}

function KodiakTlaSkuCell({
  tlaSku,
  pcbaSku,
}: {
  tlaSku: string | null;
  pcbaSku: string;
}): JSX.Element {
  if (tlaSku == null || tlaSku.trim() === "") {
    return <span>—</span>;
  }

  const appearance = resolveKodiakTlaSkuChipAppearance(tlaSku, pcbaSku);
  const displayLabel = stripAltTlaSkuPrefixForDisplay(tlaSku);
  if (appearance.family === "unknown") {
    return <span className="font-mono text-slate-800">{displayLabel}</span>;
  }

  return <KodiakFamilyChip label={displayLabel} appearance={appearance} />;
}

function KodiakTlaCells({
  row,
  lineCompleteLockIds,
  lineCompleteLockAdminAllowed,
  lineCompleteLockPendingBuildOrderId,
  onSerialCountContextMenu,
}: {
  row: KodiakTlaPcbaMatchRow;
  lineCompleteLockIds: ReadonlySet<number>;
  lineCompleteLockAdminAllowed: boolean;
  lineCompleteLockPendingBuildOrderId: number | null;
  onSerialCountContextMenu: (
    event: React.MouseEvent<HTMLTableCellElement>,
    target: { tlaBuildOrderId: number; locked: boolean },
  ) => void;
}): JSX.Element {
  if (row.row_kind === "unallocated_qc") {
    return (
      <>
        <td className="px-3 py-2 text-slate-600">
          <HoverHintContent
            hoverHint="PCBA QC incidents on this PCBA prefix that could not be split between TLA A vs C lines. Typical when the incident has no configured finished SKU. Counted in the prefix hover; not included in TLA line serial counts above."
            align="start"
          >
            <span className="italic">PCBA QC (unallocated)</span>
          </HoverHintContent>
        </td>
        <td className="px-3 py-2 text-slate-400">—</td>
        <td className={TLA_PLANNING_NUM_CELL_CLASS}>—</td>
        <KodiakTlaSerialCountCell
          row={row}
          lineCompleteLockIds={lineCompleteLockIds}
          lineCompleteLockAdminAllowed={lineCompleteLockAdminAllowed}
          lineCompleteLockPendingBuildOrderId={lineCompleteLockPendingBuildOrderId}
          onRequestContextMenu={onSerialCountContextMenu}
        />
      </>
    );
  }

  return (
    <>
      <td className="px-3 py-2 tabular-nums">
        {row.tla_work_order_number != null ? (
          <div className="flex flex-col gap-0.5">
            <KodiakBuildOrderLink
              buildOrderId={row.tla_build_order_id}
              buildOrderMvId={row.tla_build_order_mv_id}
              workOrderNumber={row.tla_work_order_number}
            />
            {row.tla_po_number != null && row.tla_purchase_order_id != null ? (
              <span className="text-xs text-slate-500">
                <PurchaseOrderHubLinkWithMegaventory
                  purchaseOrderId={row.tla_purchase_order_id}
                  poNumber={row.tla_po_number}
                  purchaseOrderMvId={row.tla_purchase_order_mv_id ?? null}
                />
              </span>
            ) : null}
          </div>
        ) : (
          "—"
        )}
      </td>
      <td className="px-3 py-2">
        <KodiakTlaSkuCell tlaSku={row.tla_sku} pcbaSku={row.pcba_sku} />
      </td>
      <td className={TLA_PLANNING_NUM_CELL_CLASS}>
        {row.tla_work_order_qty == null ? "—" : formatNumber(row.tla_work_order_qty)}
      </td>
      <KodiakTlaSerialCountCell
        row={row}
        lineCompleteLockIds={lineCompleteLockIds}
        lineCompleteLockAdminAllowed={lineCompleteLockAdminAllowed}
        lineCompleteLockPendingBuildOrderId={lineCompleteLockPendingBuildOrderId}
        onRequestContextMenu={onSerialCountContextMenu}
      />
    </>
  );
}

function KodiakTlaChildRow({
  child,
  parent,
  rowClassName,
  prefixViewModel,
  prefixAssignmentDisabled,
  onAssignPrefixToTla,
  onDragStartPrefix,
  lineCompleteLockIds,
  lineCompleteLockAdminAllowed,
  lineCompleteLockPendingBuildOrderId,
  onSerialCountContextMenu,
}: {
  child: KodiakTlaPcbaMatchRow;
  parent: KodiakTlaPcbaMatchRow;
  rowClassName: string | undefined;
  prefixViewModel: KodiakPrefixAssignmentViewModel;
  prefixAssignmentDisabled: boolean;
  onAssignPrefixToTla: (payload: KodiakPrefixDragPayload, target: KodiakTlaPrefixDropTarget) => void;
  onDragStartPrefix: (event: React.DragEvent<HTMLSpanElement>, payload: KodiakPrefixDragPayload) => void;
  lineCompleteLockIds: ReadonlySet<number>;
  lineCompleteLockAdminAllowed: boolean;
  lineCompleteLockPendingBuildOrderId: number | null;
  onSerialCountContextMenu: (
    event: React.MouseEvent<HTMLTableCellElement>,
    target: { tlaBuildOrderId: number; locked: boolean },
  ) => void;
}): JSX.Element {
  const dropTarget = useMemo((): KodiakTlaPrefixDropTarget | null => {
    if (
      child.row_kind === "unallocated_qc" ||
      child.tla_build_order_id == null ||
      child.tla_sku == null ||
      child.tla_sku.trim() === "" ||
      parent.pcba_build_order_id == null ||
      parent.purchase_order_id == null
    ) {
      return null;
    }
    return {
      purchase_order_id: Number(parent.purchase_order_id),
      pcba_build_order_id: Number(parent.pcba_build_order_id),
      tla_build_order_id: Number(child.tla_build_order_id),
      tla_sku: child.tla_sku,
      tla_work_order_number: child.tla_work_order_number,
      target_core_feature: kodiakPcbaSkuCoreFeature(parent.pcba_sku),
    };
  }, [child, parent]);

  const tlaAssignments = useMemo(() => {
    if (
      child.row_kind === "unallocated_qc" ||
      child.tla_build_order_id == null ||
      child.tla_sku == null ||
      parent.pcba_build_order_id == null
    ) {
      return [];
    }
    return kodiakTlaRowPrefixAssignments(
      Number(parent.pcba_build_order_id),
      Number(child.tla_build_order_id),
      child.tla_sku,
      prefixViewModel,
    );
  }, [child, parent, prefixViewModel]);

  const dropRow = useKodiakTlaPrefixDropRow({
    dropTarget,
    prefixViewModel,
    disabled: prefixAssignmentDisabled,
    onAssignPrefixToTla,
  });

  return (
    <tr
      className={cn(rowClassName, dropRow.rowClassName)}
      onDragOver={dropRow.onDragOver}
      onDragLeave={dropRow.onDragLeave}
      onDrop={dropRow.onDrop}
    >
      <td className="px-3 py-2" colSpan={6} aria-hidden />
      <td className="px-3 py-2">
        {tlaAssignments.length > 0 ? (
          <div className="flex min-h-7 flex-wrap gap-1">
            <KodiakAssignedPrefixChips
              assignments={tlaAssignments}
              discovered_by_variant={prefixViewModel.discovered_by_variant}
              discovered_hover_by_variant={prefixViewModel.discovered_hover_by_variant}
              planned_by_variant={prefixViewModel.planned_by_variant}
              hover_by_assignment_key={prefixViewModel.hover_by_assignment_key}
              disabled={prefixAssignmentDisabled}
              onDragStartPrefix={onDragStartPrefix}
            />
          </div>
        ) : null}
      </td>
      <KodiakTlaCells
        row={child}
        lineCompleteLockIds={lineCompleteLockIds}
        lineCompleteLockAdminAllowed={lineCompleteLockAdminAllowed}
        lineCompleteLockPendingBuildOrderId={lineCompleteLockPendingBuildOrderId}
        onSerialCountContextMenu={onSerialCountContextMenu}
      />
    </tr>
  );
}

function KodiakPcbaCells({
  row,
  groupRows,
  indentTla,
  prefixViewModel,
  prefixAssignmentDisabled,
  onAssignPrefix,
  onDragStartPrefix,
  lineCompleteLockIds,
  lineCompleteLockAdminAllowed,
  lineCompleteLockPendingBuildOrderId,
  onSerialCountContextMenu,
}: {
  row: KodiakTlaPcbaMatchRow;
  groupRows: KodiakTlaPcbaMatchRow[];
  indentTla?: boolean;
  prefixViewModel: KodiakPrefixAssignmentViewModel;
  prefixAssignmentDisabled: boolean;
  onAssignPrefix: (
    payload: KodiakPrefixDragPayload,
    target: { purchase_order_id: number; pcba_build_order_id: number },
  ) => void;
  onDragStartPrefix: (event: React.DragEvent<HTMLSpanElement>, payload: KodiakPrefixDragPayload) => void;
  lineCompleteLockIds: ReadonlySet<number>;
  lineCompleteLockAdminAllowed: boolean;
  lineCompleteLockPendingBuildOrderId: number | null;
  onSerialCountContextMenu: (
    event: React.MouseEvent<HTMLTableCellElement>,
    target: { tlaBuildOrderId: number; locked: boolean },
  ) => void;
}): JSX.Element {
  return (
    <>
      <td className="px-3 py-2">
        <KodiakPurchaseOrderLink row={row} />
      </td>
      <td className="px-3 py-2 tabular-nums">{formatTlaPlanningDate(row.po_date)}</td>
      <td className={TLA_PLANNING_NUM_CELL_CLASS}>{formatNumber(row.po_quantity)}</td>
      <td className="px-3 py-2 tabular-nums">
        <KodiakBuildOrderLink
          buildOrderId={row.pcba_build_order_id}
          buildOrderMvId={row.pcba_build_order_mv_id}
          workOrderNumber={row.pcba_work_order_number}
        />
      </td>
      <td className="px-3 py-2">{row.pcba_sku}</td>
      <td className={TLA_PLANNING_NUM_CELL_CLASS}>{formatNumber(row.pcba_work_order_qty)}</td>
      <td className="px-3 py-2 tabular-nums">
        <KodiakPcbaPrefixCell
          row={row}
          groupRows={groupRows}
          prefixViewModel={prefixViewModel}
          prefixAssignmentDisabled={prefixAssignmentDisabled}
          onAssignPrefix={onAssignPrefix}
          onDragStartPrefix={onDragStartPrefix}
        />
      </td>
      {indentTla ? (
        <>
          <td className="px-3 py-2 text-slate-400" colSpan={4}>
            —
          </td>
        </>
      ) : (
        <KodiakTlaCells
          row={row}
          lineCompleteLockIds={lineCompleteLockIds}
          lineCompleteLockAdminAllowed={lineCompleteLockAdminAllowed}
          lineCompleteLockPendingBuildOrderId={lineCompleteLockPendingBuildOrderId}
          onSerialCountContextMenu={onSerialCountContextMenu}
        />
      )}
    </>
  );
}

export function KodiakTlaPcbaMatchTableBody({
  groups,
  prefixViewModel,
  prefixAssignmentDisabled,
  onAssignPrefix,
  onAssignPrefixToTla,
  onDragStartPrefix,
  lineCompleteLockIds = EMPTY_KODIAK_LINE_COMPLETE_LOCK_IDS,
  lineCompleteLockAdminAllowed = false,
  lineCompleteLockPendingBuildOrderId = null,
  onSerialCountContextMenu = () => {},
}: {
  groups: KodiakPcbaRowGroup[];
  prefixViewModel: KodiakPrefixAssignmentViewModel;
  prefixAssignmentDisabled: boolean;
  onAssignPrefix: (
    payload: KodiakPrefixDragPayload,
    target: { purchase_order_id: number; pcba_build_order_id: number },
  ) => void;
  onAssignPrefixToTla: (payload: KodiakPrefixDragPayload, target: KodiakTlaPrefixDropTarget) => void;
  onDragStartPrefix: (event: React.DragEvent<HTMLSpanElement>, payload: KodiakPrefixDragPayload) => void;
  lineCompleteLockIds?: ReadonlySet<number>;
  lineCompleteLockAdminAllowed?: boolean;
  lineCompleteLockPendingBuildOrderId?: number | null;
  onSerialCountContextMenu?: (
    event: React.MouseEvent<HTMLTableCellElement>,
    target: { tlaBuildOrderId: number; locked: boolean },
  ) => void;
}): ReactNode {
  const sharedLockProps = {
    lineCompleteLockIds,
    lineCompleteLockAdminAllowed,
    lineCompleteLockPendingBuildOrderId,
    onSerialCountContextMenu,
  };

  return (
    <>
      {groups.map((group) => {
        if (group.layout === "flat") {
          const row = group.rows[0];
          if (row == null) return null;
          return (
            <tr key={group.key} className="border-b border-slate-100">
              <KodiakPcbaCells
                row={row}
                groupRows={group.rows}
                prefixViewModel={prefixViewModel}
                prefixAssignmentDisabled={prefixAssignmentDisabled}
                onAssignPrefix={onAssignPrefix}
                onDragStartPrefix={onDragStartPrefix}
                {...sharedLockProps}
              />
            </tr>
          );
        }

        const parent = group.rows[0];
        if (parent == null) return null;
        return (
          <Fragment key={group.key}>
            <tr className="border-b border-slate-200 bg-slate-50/80">
              <KodiakPcbaCells
                row={parent}
                groupRows={group.rows}
                indentTla
                prefixViewModel={prefixViewModel}
                prefixAssignmentDisabled={prefixAssignmentDisabled}
                onAssignPrefix={onAssignPrefix}
                onDragStartPrefix={onDragStartPrefix}
                {...sharedLockProps}
              />
            </tr>
            {group.rows.map((child, index) => (
              <KodiakTlaChildRow
                key={`${group.key}:${child.row_kind === "unallocated_qc" ? "unallocated-qc" : `tla:${child.tla_work_order_number ?? index}`}`}
                child={child}
                parent={parent}
                rowClassName={cn(
                  "border-b border-slate-100",
                  index === group.rows.length - 1 ? undefined : "border-slate-50",
                )}
                prefixViewModel={prefixViewModel}
                prefixAssignmentDisabled={prefixAssignmentDisabled}
                onAssignPrefixToTla={onAssignPrefixToTla}
                onDragStartPrefix={onDragStartPrefix}
                {...sharedLockProps}
              />
            ))}
          </Fragment>
        );
      })}
    </>
  );
}

export function kodiakMatchSummaryLabel(rowCount: number, groupCount: number): string {
  const groupPart = groupCount === rowCount ? `${rowCount} rows` : `${rowCount} TLA lines · ${groupCount} PCBA groups`;
  return `${groupPart} · serial counts mapped to TLA SKU via create_bom_current`;
}
